import type { StripeTokenAccount } from '@features/booking/state/types';

const GUARANTEE_TYPE_DEPOSIT = 'deposit';
const GUARANTEE_TYPE_NO_SHOW = 'no_show';

export type AmexChangeInput = {
  cardBrand: string | null | undefined;
  allowAMEXBR: boolean | undefined;
  guaranteeType: string | null | undefined;
  tokenCreatedWithPlatformKey: boolean;
};

export type StripeTokenAccountInput = {
  cardBrand: string | null | undefined;
  allowAMEXBR: boolean | undefined;
  guaranteeType: string | null | undefined;
};

export function isAmexBrand(cardBrand: string | null | undefined): boolean {
  if (!cardBrand) return false;
  const normalizedBrand = cardBrand.toLowerCase();
  return normalizedBrand === 'amex' || normalizedBrand === 'american express';
}

export function shouldUsePlatformStripeToken({
  cardBrand,
  allowAMEXBR,
  guaranteeType,
}: StripeTokenAccountInput): boolean {
  return isAmexBrand(cardBrand) && !allowAMEXBR && guaranteeType === GUARANTEE_TYPE_NO_SHOW;
}

export function getStripeTokenAccount(input: StripeTokenAccountInput): StripeTokenAccount {
  return shouldUsePlatformStripeToken(input) ? 'platform' : 'restaurant';
}

export function getStripePublishableKeyForTokenAccount({
  tokenAccount,
  restaurantPublishableKey,
  platformPublishableKey,
}: {
  tokenAccount: StripeTokenAccount | null | undefined;
  restaurantPublishableKey: string;
  platformPublishableKey: string;
}): string {
  return tokenAccount === 'platform' ? platformPublishableKey : restaurantPublishableKey;
}

// Legacy used `guaranteeType !== 'deposit'`; shouldUsePlatformStripeToken
// uses `=== 'no_show'`. Equivalent for current values ('deposit' | 'no_show'),
// but callers must only set tokenCreatedWithPlatformKey once the reservation
// has loaded and guaranteeType is known.
export function computeAmexChange({
  cardBrand,
  allowAMEXBR,
  guaranteeType,
  tokenCreatedWithPlatformKey,
}: AmexChangeInput): boolean {
  const semanticAmexChange =
    shouldUsePlatformStripeToken({ cardBrand, allowAMEXBR, guaranteeType });

  return semanticAmexChange && tokenCreatedWithPlatformKey;
}
